import { z } from "zod";

const timestampSchema = z.number().positive().int().default(Date.now); // timestamp as returned from Date.now()
const secondsSchema = z.number().positive().int(); // number of seconds to count duration

// ----------------------
// Legacy voting sessions
// ----------------------

enum UserVotingStatus {
  Unknown,
  Joined,
  Declined,
  Finished,
}
const UserVotingStatusEnum = z.nativeEnum(UserVotingStatus);

const canvasUserSchema = z.object({
  type: z.literal("known"),
  status: UserVotingStatusEnum,
  id: z.string(),
});
const guestUserSchema = z.object({
  type: z.literal("anonymous"),
  status: UserVotingStatusEnum,
  name: z.string(),
  avatar: z.string(),
});

const voterInfoSchema = canvasUserSchema.or(guestUserSchema);

const legacyVotingSchema = z.object({
  ownerId: z.string(),
  title: z.string(),
  start: timestampSchema,
  duration: secondsSchema.optional(),
  maxVotesPerChoice: z.number().int().positive().default(1),
  maxVotesPerUser: z.number().int().positive().default(1),
  elementsIds: z.array(z.string()).optional(), // the elements we're voting on (if null, all elements in board)
  anonymous: z.boolean().optional().default(false), // do we display the results in the gui

  votersInfo: z.record(voterInfoSchema), // user-key -> basic info
  votesCast: z.record(z.array(z.string())), // Record: element-id -> [user-id1, user-id2, user-id3, ...]
  hidden: z.boolean().default(false),
  itemsThumbnails: z.record(z.string()), // thumbnails of the canvas elements voted on
});

// ----------------------
// New voting schema
// ----------------------

// Voting data:
// voting/s/[session-id]: VotingSession
// voting/u/[session-id]/[user-id]: VoterData
// voting-status for user: in an atom: [session-id, is-participating]

// objects describing users that voted. We have either canvas-users or anonymous guests.

const voterId = z.discriminatedUnion("type", [
  z.object({ type: z.literal("known"), id: z.string() }),
  z.object({ type: z.literal("anonymous"), id: z.string(), name: z.string(), avatar: z.string() }),
]);

const votingSessionConfigSchema = z.object({
  ownerId: z.string(),
  title: z.string(),
  start: timestampSchema,
  duration: secondsSchema.default(5 * 60),
  numVotes: z.number().int().positive().default(1),
  anonymous: z.boolean().optional().default(false),
  maxVotesOnObject: z.number().int().default(Number.MAX_SAFE_INTEGER),
  hidden: z.boolean().default(false),
});

const voterSchema = z.object({
  id: voterId,
  votes: z.record(z.string(), z.number()),
});

type VotingSessionConfig = z.input<typeof votingSessionConfigSchema>;
type VotingSession = z.infer<typeof votingSessionConfigSchema>;
type VoterId = z.infer<typeof voterId>;
type VoterData = z.infer<typeof voterSchema>;

type LegacyVotingSession = z.infer<typeof legacyVotingSchema>;

export { votingSessionConfigSchema, voterSchema };
export type { VotingSessionConfig, VotingSession, VoterId, VoterData, LegacyVotingSession };
